import * as XLSX from 'xlsx';

const AU_API = (window.AUApi && window.AUApi.segregationPlanSite) || {};
const API = {
    ACTIVE_PURCHASE: AU_API.activePurchase,
    SITES: AU_API.sites,
    SITE_GRADE: AU_API.siteGrade,
    COMMODITY: AU_API.commodity,
};

function buildUrl(template, tokens) {
    if (!template) return null;
    return Object.keys(tokens || {}).reduce(
        (url, key) => url.split(`{${key}}`).join(tokens[key]),
        template
    );
}

const state = {
    siteDescription: null,
    gradeItems: [],
    siteGradeItems: [],
    commodities: [],
};

const root = document.querySelector('[data-control="segregationPlan"]');

const $ = sel => root.querySelector(sel);
const show = el => el?.setAttribute('aria-hidden', 'false');
const hide = el => el?.setAttribute('aria-hidden', 'true');

function cloneTemplate(name) {
    const template = root.querySelector(`[data-template="${name}"]`);
    if (!template) return null;
    const clone = template.cloneNode(true);
    clone.removeAttribute('data-template');
    return clone;
}

function findRegion(el, name) {
    return el?.querySelector(`[data-region="${name}"]`) ?? null;
}

function toArray(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.value)) return data.value;
    return [];
}

async function apiFetch(url) {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status} — ${url}`);
    return res.json();
}

function resolveSiteName() {
    const attr = root.getAttribute('data-site')?.trim();
    if (attr) return attr;

    const segments = window.location.pathname.split('/').filter(Boolean);
    let last = segments.at(-1) ?? null;
    if (last) {
        try {
            last = decodeURIComponent(last);
        } catch {}
        last = last.replace(/\.html?$/i, '').trim();
    }
    return last || null;
}

function formatSiteDisplayName(siteName) {
    if (!siteName) return '';
    return siteName
        .toLowerCase()
        .replace(/[a-z]+/g, word => word.charAt(0).toUpperCase() + word.slice(1));
}

function resolveSiteDisplayName(siteName) {
    return (
        root.getAttribute('data-site-display-name')?.trim() ||
        formatSiteDisplayName(siteName)
    );
}

function isPlaceholderValue(value) {
    const text = String(value ?? '').trim();
    return !text || text === '*' || text === '*, -w-,' || text === ',-w-,';
}

function setSiteTitle(name) {
    const siteName = isPlaceholderValue(name) ? '' : String(name || '').trim();
    if (siteName) document.title = siteName;
}

function updateOpenGraphTitle(name) {
    if (isPlaceholderValue(name)) return;

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle && isPlaceholderValue(ogTitle.getAttribute('content'))) {
        ogTitle.setAttribute('content', document.title);
    }
}

function activePurchaseUrl(siteDesc) {
    return buildUrl(API.ACTIVE_PURCHASE, {
        siteDesc: encodeURIComponent(siteDesc),
    });
}

function siteGradeUrl(siteDesc) {
    return buildUrl(API.SITE_GRADE, { siteDesc: encodeURIComponent(siteDesc) });
}

function formatRefreshTime(date) {
    try {
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d)) return String(date);

        const pad = n => String(n).padStart(2, '0');
        const dateText = `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(
            d.getDate()
        )}`;
        const timeText = d.toLocaleTimeString();
        const tz =
            Intl && Intl.DateTimeFormat
                ? Intl.DateTimeFormat().resolvedOptions().timeZone
                : 'local';
        return `${dateText} ${timeText} (${tz})`;
    } catch {
        return String(date);
    }
}

function updateRefreshTime() {
    const el = $('[data-region="pricingRefresh"]');
    if (el) el.textContent = formatRefreshTime(new Date());
}

function hasAvailablePrice(value) {
    const parsed = parseFloat(value);
    return value !== null && value !== '' && !isNaN(parsed) && parsed > 0;
}

function resolveLatLng(site) {
    return {
        lat: parseFloat(site.latitude ?? site.Latitude ?? site.lat ?? NaN),
        lng: parseFloat(site.longitude ?? site.Longitude ?? site.lng ?? NaN),
    };
}

function renderSiteUpdate(site) {
    const boxEl = $('[data-region="siteUpdate"]');
    const msgEl = $('[data-region="siteUpdateMessage"]');
    if (!boxEl || !msgEl) return;

    const today = new Date()
        .toLocaleDateString('en-AU', { weekday: 'long' })
        .toLowerCase();
    let message = '';

    for (let i = 0; i <= 6; i++) {
        const dayName = (
            site[`day${i}`] ??
            site[`Day${i}`] ??
            ''
        ).toLowerCase();
        if (dayName === today) {
            message = (site[`message${i}`] ?? site[`Message${i}`] ?? '').trim();
            break;
        }
    }

    if (!message) return;
    msgEl.textContent = message;
    show(boxEl);
}

function getSiteValue(site, key) {
    const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
    return site[key] ?? site[pascalKey] ?? '';
}

function getRecordValue(record, key) {
    const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
    return record[key] ?? record[pascalKey] ?? '';
}

function getBuyerName(record) {
    return (
        record.ownerDescription ??
        record.OwnerDescription ??
        record.owner ??
        record.Owner ??
        ''
    );
}

function sortMatchesByPriceThenBuyer(matches) {
    return [...matches].sort((a, b) => {
        const priceDiff =
            parseFloat(b.price ?? b.Price ?? 0) -
            parseFloat(a.price ?? a.Price ?? 0);
        if (priceDiff) return priceDiff;
        return getBuyerName(a).localeCompare(getBuyerName(b));
    });
}

function isTruthyValue(value) {
    return value === true || String(value).toLowerCase() === 'true';
}

function normalizeStatus(status) {
    return String(status ?? '')
        .toLowerCase()
        .replace(/\s/g, '');
}

function humanizeStatus(status) {
    const spaced = String(status ?? '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim();
    return spaced
        ? spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
        : spaced;
}

function readStatusSettings() {
    const configEl = root?.querySelector('[data-region="statusConfig"]');
    const openStatuses = new Set(
        (configEl?.getAttribute('data-open-status') || '')
            .split(/\s+/)
            .map(normalizeStatus)
            .filter(Boolean)
    );

    return {
        defaultStatus: configEl?.getAttribute('data-default-status') || '',
        openStatuses,
    };
}

const statusSettings = readStatusSettings();

function readHoursSettings() {
    const configEl = root?.querySelector('[data-region="hoursConfig"]');
    const days = {};
    const getLabel = key =>
        configEl
            ?.querySelector(`[data-hours-label="${key}"]`)
            ?.textContent.trim() || '';

    configEl?.querySelectorAll('[data-day-key]').forEach(el => {
        const key = el.getAttribute('data-day-key') || '';
        const entry = {
            shortLabel: el.textContent.trim(),
        };
        const normalized = normalizeStatus(key);
        if (normalized) days[normalized] = entry;
    });

    return {
        todayLabel: getLabel('today'),
        upcomingHeading: getLabel('upcoming-heading'),
        days,
    };
}

const hoursSettings = readHoursSettings();

function getDayShortLabel(day) {
    return hoursSettings.days[normalizeStatus(day)]?.shortLabel || day;
}

function findHoursByDate(hours, date) {
    const dayName = date
        .toLocaleDateString('en-AU', { weekday: 'long' })
        .toLowerCase();
    return hours.find(({ day }) => day.toLowerCase() === dayName) ?? null;
}

function getHoursDisplay({ status, time }) {
    const hasTime = time && time.toLowerCase() !== 'not available';
    return isOpenStatus(status) && hasTime ? time : getStatusDisplay(status);
}

function getStatusDisplay(status) {
    return humanizeStatus(status);
}

function isOpenStatus(status) {
    return statusSettings.openStatuses.has(normalizeStatus(status));
}

function renderHoursRows(listEl, rows) {
    if (!listEl) return;
    listEl.replaceChildren();
    const fragment = document.createDocumentFragment();
    rows.forEach(({ label, info }) => {
        const row = cloneTemplate('hoursRow');
        if (!row) return;
        const dayEl = findRegion(row, 'hoursDay');
        const statusEl = findRegion(row, 'hoursStatus');
        if (dayEl) dayEl.textContent = label;
        if (statusEl) statusEl.textContent = getHoursDisplay(info);
        fragment.appendChild(row);
    });
    listEl.appendChild(fragment);
}

function renderCard(site, lat, lng) {
    const cardEl = $('[data-region="siteCard"]');
    if (!cardEl) return;

    const directionsLink = $('[data-region="directionsLink"]');
    const managerBlock = $('[data-region="managerBlock"]');
    const managerNameEl = $('[data-region="managerName"]');
    const phoneBlock = $('[data-region="phoneBlock"]');
    const phoneLink = $('[data-region="phoneLink"]');
    const mobileBlock = $('[data-region="mobileBlock"]');
    const mobileLink = $('[data-region="mobileLink"]');
    const statusDot = $('[data-region="statusDot"]');
    const statusTextEl = $('[data-region="statusText"]');
    const hoursBlock = $('[data-region="hoursBlock"]');
    const upcomingHoursHeading = $('[data-region="upcomingHoursHeading"]');
    const todayHoursList = $('[data-region="todayHoursList"]');
    const hoursList = $('[data-region="hoursList"]');

    const manager = getSiteValue(site, 'manager');
    const phone = getSiteValue(site, 'phone');
    const mobile = getSiteValue(site, 'managerMobile');

    const hours = Array.from({ length: 7 }, (_, i) => ({
        day: getSiteValue(site, `day${i}`),
        status:
            getSiteValue(site, `status${i}`) || statusSettings.defaultStatus,
        time: getSiteValue(site, `time${i}`),
    })).filter(h => h.day);

    const today = new Date();
    const todayHours = findHoursByDate(hours, today) ?? hours[0];
    const todayStatus = todayHours?.status || statusSettings.defaultStatus;
    const todayStatusKey = normalizeStatus(todayStatus);
    const todayOpen = isOpenStatus(todayStatus);
    const statusClass = `site-detail__status-dot--${todayStatusKey}`;
    const statusText = getStatusDisplay(todayStatus);
    const textClass = `site-detail__status-text--${todayStatusKey}`;
    const directionsUrl = `https://www.google.com/maps?saddr=My+Location&daddr=${lat},${lng}`;

    if (directionsLink) directionsLink.href = directionsUrl;

    if (managerBlock) managerBlock.hidden = !manager;
    if (managerNameEl) managerNameEl.textContent = manager;

    if (phoneBlock) phoneBlock.hidden = !phone;
    if (phoneLink) {
        phoneLink.href = phone ? `tel:${phone}` : '#';
        phoneLink.textContent = phone;
    }

    if (mobileBlock) mobileBlock.hidden = !mobile;
    if (mobileLink) {
        mobileLink.href = mobile ? `tel:${mobile}` : '#';
        mobileLink.textContent = mobile;
    }

    if (statusDot) {
        statusDot.className = `site-detail__status-dot ${statusClass}`;
    }

    if (statusTextEl) {
        statusTextEl.className = `site-detail__status-text ${textClass}`;
        statusTextEl.textContent = statusText;
    }

    const upcomingHours = Array.from({ length: 6 }, (_, i) => {
        const date = new Date(today);
        date.setDate(today.getDate() + i + 1);
        const info = findHoursByDate(hours, date);
        return info
            ? {
                  label: getDayShortLabel(info.day),
                  info,
              }
            : null;
    }).filter(Boolean);
    const todayRows =
        todayOpen && todayHours
            ? [{ label: hoursSettings.todayLabel, info: todayHours }]
            : [];

    if (hoursBlock) {
        hoursBlock.hidden = !todayRows.length && !upcomingHours.length;
    }
    if (upcomingHoursHeading) {
        upcomingHoursHeading.textContent = hoursSettings.upcomingHeading;
    }
    renderHoursRows(todayHoursList, todayRows);
    renderHoursRows(hoursList, upcomingHours);
}

function loadGoogleMapsScript() {
    if (typeof google !== 'undefined' && google.maps) return Promise.resolve();

    const existing = document.querySelector(
        'script[src*="maps.googleapis.com/maps/api/js"]'
    );
    if (existing) {
        return new Promise(resolve => {
            const check = setInterval(() => {
                if (typeof google !== 'undefined' && google.maps) {
                    clearInterval(check);
                    resolve();
                }
            }, 100);
        });
    }

    const apiKey =
        root.getAttribute('data-maps-key') ??
        root.getAttribute('data-api-key') ??
        '';
    if (!apiKey)
        return Promise.reject(new Error('No Google Maps API key found.'));

    return new Promise((resolve, reject) => {
        window.__segregationMapsReady = resolve;
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
            apiKey
        )}&loading=async&callback=__segregationMapsReady`;
        script.async = true;
        script.defer = true;
        script.onerror = () =>
            reject(new Error('Google Maps script failed to load.'));
        document.head.appendChild(script);
    });
}

async function initMap(lat, lng, siteName) {
    const mapEl = document.getElementById('site-map');
    if (!mapEl) return;
    const mapErrorEl = $('[data-region="mapError"]');
    hide(mapErrorEl);
    mapEl.removeAttribute('aria-hidden');

    const showMapError = () => {
        mapEl.setAttribute('aria-hidden', 'true');
        show(mapErrorEl);
    };

    window.gm_authFailure = showMapError;

    try {
        await loadGoogleMapsScript();

        const { Map } = await google.maps.importLibrary('maps');
        const position = { lat, lng };

        const map = new Map(mapEl, {
            zoom: 11,
            center: position,
            keyboardShortcuts: true,
            disableDefaultUI: true,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            cameraControl: false,
        });

        new google.maps.Marker({ map, position, title: siteName ?? '' });

        const readableName = siteName ? formatSiteDisplayName(siteName) : '';
        const mapLabel = (
            mapEl.getAttribute('data-map-label') ||
            mapEl.getAttribute('aria-label') ||
            ''
        )
            .replace('{site}', readableName)
            .trim();
        const applyMapLabel = () => {
            if (!mapLabel) return;
            mapEl.setAttribute('aria-label', mapLabel);
            const region = mapEl.querySelector('[role="region"]');
            if (region) region.setAttribute('aria-label', mapLabel);
        };
        applyMapLabel();
        google.maps.event.addListenerOnce(map, 'idle', applyMapLabel);
    } catch {
        showMapError();
    }
}

async function fetchCommodities(siteDesc) {
    const [siteGradeData, commodityListData] = await Promise.all([
        apiFetch(siteGradeUrl(siteDesc)),
        apiFetch(API.COMMODITY).catch(() => null),
    ]);

    const siteGradeItems = toArray(siteGradeData);

    const nameMap = new Map();
    if (commodityListData) {
        toArray(commodityListData).forEach(c => {
            const id = c.commodityId ?? c.CommodityId;
            const name =
                c.commodity ??
                c.Commodity ??
                c.commodityDescription ??
                c.CommodityDescription ??
                c.description ??
                c.Description ??
                c.name ??
                c.Name;
            if (id && name) nameMap.set(String(id), name);
        });
    }

    const seen = new Map();
    for (const item of siteGradeItems) {
        const id = item.CommodityId ?? item.commodityId;
        if (!id) continue;

        const key = String(id);
        const existing = seen.get(key) ?? {
            id: key,
            code: item.CommodityCode ?? item.commodityCode ?? '',
            rank: Number(item.CommodityRank ?? item.commodityRank),
            index: seen.size,
        };
        existing.rank = Math.min(
            existing.rank,
            Number(item.CommodityRank ?? item.commodityRank)
        );
        if (!existing.code) {
            existing.code = item.CommodityCode ?? item.commodityCode ?? '';
        }
        seen.set(key, existing);
    }

    const commodities = Array.from(seen.values())
        .sort((a, b) => a.rank - b.rank)
        .map(item => ({
            id: item.id,
            code: item.code,
            name: nameMap.get(item.id) ?? item.code ?? item.id,
            rank: item.rank,
        }));

    return { commodities, siteGradeItems };
}

function wireInfoTooltips() {
    root.querySelectorAll('.site-detail__filter-info').forEach((btn, idx) => {
        if (btn.dataset.tooltipWired) return;
        btn.dataset.tooltipWired = '1';

        const wrap = btn.closest('.site-detail__filter-info-wrap');
        if (!wrap) return;

        const infoContent =
            wrap.querySelector('[data-info-content]')?.innerHTML.trim() || '';

        const popover = cloneTemplate('infoPopover');
        if (!popover) return;
        popover.id = `site-detail-info-${idx}`;
        popover.setAttribute('aria-hidden', 'true');
        const popoverContent = findRegion(popover, 'infoPopoverContent');
        if (popoverContent) popoverContent.innerHTML = infoContent;
        btn.setAttribute('aria-describedby', popover.id);
        wrap.appendChild(popover);

        const openPopover = () => {
            popover.setAttribute('aria-hidden', 'false');
            btn.setAttribute('aria-expanded', 'true');
        };
        const closePopover = () => {
            popover.setAttribute('aria-hidden', 'true');
            btn.setAttribute('aria-expanded', 'false');
        };
        const isOpen = () => popover.getAttribute('aria-hidden') === 'false';

        btn.addEventListener('click', e => {
            e.stopPropagation();
            root.querySelectorAll(
                '.site-detail__info-popover[aria-hidden="false"]'
            ).forEach(p => p.setAttribute('aria-hidden', 'true'));
            root.querySelectorAll('.site-detail__filter-info').forEach(info =>
                info.setAttribute('aria-expanded', 'false')
            );
            if (!isOpen()) openPopover();
        });

        btn.addEventListener('keydown', e => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            closePopover();
        });

        popover
            .querySelector('.site-detail__info-popover-close')
            .addEventListener('click', e => {
                e.stopPropagation();
                closePopover();
            });

        popover.addEventListener('keydown', e => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            closePopover();
            btn.focus();
        });
    });

    if (!root.dataset.tooltipOutsideWired) {
        root.dataset.tooltipOutsideWired = '1';
        document.addEventListener('click', () => {
            root.querySelectorAll(
                '.site-detail__info-popover[aria-hidden="false"]'
            ).forEach(p => p.setAttribute('aria-hidden', 'true'));
            root.querySelectorAll('.site-detail__filter-info').forEach(info =>
                info.setAttribute('aria-expanded', 'false')
            );
        });
    }
}

function findPriceMatches(gradeId, gradeName, commodityId) {
    const cidStr = String(commodityId);
    const gradeIdStr = String(gradeId);

    const byId = state.gradeItems.filter(
        g => gradeId && String(g.gradeId ?? g.GradeId ?? '') === gradeIdStr
    );
    if (byId.length) return byId;

    return state.gradeItems.filter(
        g =>
            String(g.commodityId ?? g.CommodityId) === cidStr &&
            String(g.grade ?? g.Grade) === String(gradeName)
    );
}

function getPricingFilterState() {
    return {
        pool: root.querySelector('[data-filter="pool"]')?.checked ?? false,
        extended:
            root.querySelector('[data-filter="extended"]')?.checked ?? false,
        sustainable:
            root.querySelector('[data-filter="sustainable"]')?.checked ?? false,
    };
}

function applyPricingFilters(matches) {
    const filters = getPricingFilterState();

    return matches.filter(match => {
        const paymentCategory = String(
            getRecordValue(match, 'paymentCategory') || ''
        ).toLowerCase();
        const sellingOptionType = String(
            getRecordValue(match, 'sellingOptionType') || ''
        ).toLowerCase();

        if (!filters.pool && paymentCategory === 'pool') return false;
        if (
            !filters.extended &&
            paymentCategory === 'cash' &&
            sellingOptionType === 'ep'
        ) {
            return false;
        }
        if (
            !filters.sustainable &&
            isTruthyValue(getRecordValue(match, 'isSustainable'))
        ) {
            return false;
        }

        return true;
    });
}

function countUniqueValues(rows, keys) {
    const values = new Set();
    rows.forEach(row => {
        for (const key of keys) {
            const value = getRecordValue(row, key);
            if (value) {
                values.add(value);
                return;
            }
        }
    });
    return values.size;
}

function renderGradeTable(commodityId) {
    const tbody = $('[data-region="gradeTableBody"]');
    if (!tbody) return;

    const rows = state.siteGradeItems
        .filter(
            item =>
                String(item.CommodityId ?? item.commodityId) ===
                String(commodityId)
        )
        .sort(
            (a, b) =>
                Number(a.GradeRank ?? a.gradeRank) -
                Number(b.GradeRank ?? b.gradeRank)
        );

    tbody.replaceChildren();

    if (!rows.length) {
        const emptyRow = cloneTemplate('emptyGradeRow');
        if (emptyRow) tbody.appendChild(emptyRow);
        return;
    }

    const fragment = document.createDocumentFragment();

    rows.forEach((item, idx) => {
        const gradeId = item.primaryGradeId ?? item.PrimaryGradeId ?? '';
        const gradeName = item.primaryGrade ?? item.PrimaryGrade ?? gradeId;
        const matches = sortMatchesByPriceThenBuyer(
            applyPricingFilters(
                findPriceMatches(gradeId, gradeName, commodityId)
            )
        );

        const bestMatch = matches.length
            ? matches.reduce(
                  (best, g) =>
                      parseFloat(g.price ?? g.Price ?? 0) >
                      parseFloat(best.price ?? best.Price ?? 0)
                          ? g
                          : best,
                  matches[0]
              )
            : null;

        const price = bestMatch?.price ?? bestMatch?.Price ?? null;
        const hasPrice = hasAvailablePrice(price);
        const matchCount = countUniqueValues(matches, [
            'ownerDescription',
            'buyerName',
            'buyer',
            'owner',
        ]);

        const tr = cloneTemplate('gradeRow');
        if (!tr) return;
        const gradeNameEl = findRegion(tr, 'gradeName');
        const gradeBadgeEl = findRegion(tr, 'gradeBadge');
        const gradePriceEl = findRegion(tr, 'gradePrice');
        const gradeNoPriceEl = findRegion(tr, 'gradeNoPrice');
        const moreBtn = findRegion(tr, 'gradeMore');
        const moreTextEl = findRegion(tr, 'gradeMoreText');
        const moreIconEl = findRegion(tr, 'gradeMoreIcon');

        if (gradeNameEl) gradeNameEl.textContent = gradeName;
        if (gradeBadgeEl) {
            gradeBadgeEl.hidden = !matchCount;
            gradeBadgeEl.textContent = matchCount ? String(matchCount) : '';
        }
        if (gradePriceEl) {
            gradePriceEl.hidden = !hasPrice;
            gradePriceEl.textContent = hasPrice
                ? `$${parseFloat(price).toFixed(2)}`
                : '';
        }
        if (gradeNoPriceEl) gradeNoPriceEl.hidden = hasPrice;
        if (moreBtn) moreBtn.hidden = !(hasPrice && matches.length);

        fragment.appendChild(tr);

        if (hasPrice && matches.length && moreBtn) {
            const trDetail = cloneTemplate('gradeDetailPanel');
            if (!trDetail) return;
            trDetail.id = `site-detail-grade-detail-${commodityId}-${idx}`;
            trDetail.setAttribute('aria-hidden', 'true');
            const detailBody = findRegion(trDetail, 'gradeDetailBody');
            if (detailBody) {
                detailBody.replaceChildren();
                const detailFragment = document.createDocumentFragment();
                matches.forEach(g => {
                    const detailRow = cloneTemplate('gradeDetailRow');
                    if (!detailRow) return;
                    const p = g.price ?? g.Price ?? '';
                    const priceStr = !isNaN(parseFloat(p))
                        ? `${parseFloat(p).toFixed(2)}`
                        : '';
                    const buyer = getBuyerName(g);
                    const ptype =
                        g.sellingOptionDescription ??
                        g.SellingOptionDescription ??
                        '';
                    const terms =
                        g.paymentTermDescription ??
                        g.PaymentTermDescription ??
                        '';
                    const buyerEl = findRegion(detailRow, 'gradeDetailBuyer');
                    const priceEl = findRegion(detailRow, 'gradeDetailPrice');
                    const typeEl = findRegion(detailRow, 'gradeDetailType');
                    const termsEl = findRegion(detailRow, 'gradeDetailTerms');
                    if (buyerEl) buyerEl.textContent = buyer;
                    if (priceEl) priceEl.textContent = priceStr;
                    if (typeEl) typeEl.textContent = ptype;
                    if (termsEl) termsEl.textContent = terms;
                    detailFragment.appendChild(detailRow);
                });
                detailBody.appendChild(detailFragment);
            }

            moreBtn.setAttribute('aria-expanded', 'false');
            moreBtn.setAttribute('aria-controls', trDetail.id);
            moreBtn.setAttribute(
                'aria-label',
                `Show more details for ${gradeName}`
            );

            const toggle = () => {
                const expanded =
                    trDetail.getAttribute('aria-hidden') === 'false';
                trDetail.setAttribute(
                    'aria-hidden',
                    expanded ? 'true' : 'false'
                );
                tr.classList.toggle(
                    'site-detail__grade-row--expanded',
                    !expanded
                );
                moreBtn.setAttribute('aria-expanded', String(!expanded));
                moreBtn.setAttribute(
                    'aria-label',
                    `${
                        expanded ? 'Show' : 'Hide'
                    } more details for ${gradeName}`
                );
                if (moreTextEl) {
                    moreTextEl.textContent = !expanded
                        ? 'Fewer details'
                        : 'More details';
                }
                if (moreIconEl) {
                    moreIconEl.classList.toggle(
                        'site-detail__grade-more-icon--open',
                        !expanded
                    );
                }
            };
            moreBtn.addEventListener('click', e => {
                e.stopPropagation();
                toggle();
            });

            moreBtn.addEventListener('keydown', e => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                toggle();
            });

            tr.style.cursor = 'pointer';
            tr.addEventListener('click', toggle);

            fragment.appendChild(trDetail);
        }
    });

    tbody.appendChild(fragment);

    const gradeRows = tbody.querySelectorAll('.site-detail__grade-row');
    const lastGradeRow = gradeRows[gradeRows.length - 1];
    if (lastGradeRow)
        lastGradeRow.classList.add('site-detail__grade-row--last');
}

function renderPricing(commodities, gradeItems, refreshTime) {
    const pricingEl = $('[data-region="sitePricing"]');
    const tabsEl = $('[data-region="commodityTabs"]');
    const refreshEl = $('[data-region="pricingRefresh"]');
    const refreshBtnEl = $('[data-region="pricingRefreshBtn"]');
    const exportBtnEl = $('[data-region="exportExcel"]');

    if (!pricingEl || !tabsEl || !commodities.length) return;

    state.commodities = commodities;
    state.gradeItems = gradeItems;

    const pricingPanelId = 'site-detail-pricing-panel';
    if (pricingEl) {
        pricingEl.id = pricingPanelId;
        pricingEl.setAttribute('role', 'tabpanel');
    }

    const scrollActiveCommodityTabIntoView = activeTab => {
        if (!activeTab || tabsEl.scrollWidth <= tabsEl.clientWidth) return;

        window.requestAnimationFrame(() => {
            const wrapRect = tabsEl.getBoundingClientRect();
            const tabRect = activeTab.getBoundingClientRect();
            tabsEl.scrollLeft += tabRect.left - wrapRect.left;
        });
    };

    tabsEl.classList.remove('-open');
    tabsEl.replaceChildren();
    commodities.forEach((c, idx) => {
        const btn = cloneTemplate('commodityTab');
        if (!btn) return;
        btn.className = `site-detail__commodity-tab${
            idx === 0 ? ' -active' : ''
        }`;
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', String(idx === 0));
        btn.setAttribute('aria-controls', pricingPanelId);
        btn.setAttribute('tabindex', '0');
        btn.id = `site-detail-commodity-tab-${idx}`;
        btn.textContent = c.name;
        btn.dataset.commodityId = c.id;

        const activateTab = () => {
            tabsEl
                .querySelectorAll('.site-detail__commodity-tab')
                .forEach(t => {
                    t.classList.remove('-active');
                    t.setAttribute('aria-selected', 'false');
                });
            btn.classList.add('-active');
            btn.setAttribute('aria-selected', 'true');
            pricingEl.setAttribute('aria-labelledby', btn.id);
            renderGradeTable(c.id);
            updateRefreshTime();
            scrollActiveCommodityTabIntoView(btn);
        };

        if (idx === 0) pricingEl.setAttribute('aria-labelledby', btn.id);

        btn.addEventListener('click', () => {
            if (btn.classList.contains('-active')) {
                tabsEl.classList.toggle('-open');
                return;
            }
            tabsEl.classList.remove('-open');
            activateTab();
        });

        btn.addEventListener('keydown', e => {
            const tabs = Array.from(
                tabsEl.querySelectorAll('.site-detail__commodity-tab')
            );
            const current = tabs.indexOf(btn);
            let nextIndex = null;
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                activateTab();
                return;
            }
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                nextIndex = current + 1 >= tabs.length ? 0 : current + 1;
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                nextIndex = current - 1 < 0 ? tabs.length - 1 : current - 1;
            } else if (e.key === 'Home') {
                nextIndex = 0;
            } else if (e.key === 'End') {
                nextIndex = tabs.length - 1;
            }
            if (nextIndex === null) return;
            e.preventDefault();
            tabs[nextIndex].focus();
            tabs[nextIndex].click();
        });

        tabsEl.appendChild(btn);
    });

    if (!tabsEl.dataset.accordionWired) {
        tabsEl.dataset.accordionWired = '1';
        document.addEventListener('click', e => {
            if (!tabsEl.contains(e.target)) tabsEl.classList.remove('-open');
        });
    }

    if (refreshEl) refreshEl.textContent = formatRefreshTime(refreshTime);

    if (refreshBtnEl && !refreshBtnEl.dataset.refreshWired) {
        refreshBtnEl.dataset.refreshWired = '1';
        refreshBtnEl.addEventListener('click', refreshPricingData);
    }

    root.querySelectorAll('.site-detail__filter-check').forEach(cb => {
        if (!cb.dataset.filterWired) {
            cb.dataset.filterWired = '1';
            const label = cb.closest('label');
            if (label) {
                label.addEventListener('click', e => {
                    if (e.target.closest('.site-detail__filter-info')) return;
                    e.preventDefault();
                    cb.checked = !cb.checked;
                    cb.dispatchEvent(new Event('change', { bubbles: true }));
                });
            }
            cb.addEventListener('keydown', e => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                cb.checked = !cb.checked;
                cb.dispatchEvent(new Event('change', { bubbles: true }));
            });
            cb.addEventListener('change', () => {
                const active = tabsEl.querySelector(
                    '.site-detail__commodity-tab.-active'
                );
                if (active) {
                    renderGradeTable(active.dataset.commodityId);
                    updateRefreshTime();
                }
            });
        }
    });

    if (exportBtnEl && !exportBtnEl.dataset.exportWired) {
        exportBtnEl.dataset.exportWired = '1';
        exportBtnEl.addEventListener('click', exportToXlsx);
    }

    wireInfoTooltips();
    renderGradeTable(commodities[0].id);
    show(pricingEl);
}

async function refreshPricingData() {
    if (!state.siteDescription) return;

    try {
        const gradeItems = toArray(
            await apiFetch(activePurchaseUrl(state.siteDescription))
        );
        const result = await fetchCommodities(state.siteDescription);

        state.gradeItems = gradeItems;
        state.commodities = result.commodities;
        state.siteGradeItems = result.siteGradeItems;

        const tabsEl = $('[data-region="commodityTabs"]');
        const activeTab = tabsEl?.querySelector(
            '.site-detail__commodity-tab.-active'
        );
        const activeCid = activeTab?.dataset.commodityId ?? null;

        renderPricing(state.commodities, state.gradeItems, new Date());

        if (activeCid) {
            const tab = tabsEl?.querySelector(
                `[data-commodity-id="${activeCid}"]`
            );
            if (tab) {
                tabsEl
                    .querySelectorAll('.site-detail__commodity-tab')
                    .forEach(t => {
                        t.classList.remove('-active');
                        t.setAttribute('aria-selected', 'false');
                    });
                tab.classList.add('-active');
                tab.setAttribute('aria-selected', 'true');
                renderGradeTable(activeCid);
                updateRefreshTime();
            }
        }
    } catch {
        return;
    }
}

function exportToXlsx() {
    const headers = [
        'Site',
        'Commodity',
        'Grade',
        'Buyer',
        'Price',
        'Price type',
        'Payment terms',
    ];
    const rows = [headers];

    const sortedSiteGradeItems = [...state.siteGradeItems].sort((a, b) => {
        const commodityDiff =
            Number(a.CommodityRank ?? a.commodityRank) -
            Number(b.CommodityRank ?? b.commodityRank);
        if (commodityDiff) return commodityDiff;

        return (
            Number(a.GradeRank ?? a.gradeRank) -
            Number(b.GradeRank ?? b.gradeRank)
        );
    });

    sortedSiteGradeItems.forEach(item => {
        const gradeId = item.primaryGradeId ?? item.PrimaryGradeId ?? '';
        const gradeName = item.primaryGrade ?? item.PrimaryGrade ?? gradeId;
        const cidStr = String(item.commodityId ?? item.CommodityId ?? '');
        const commodityName =
            state.commodities.find(c => String(c.id) === cidStr)?.name ??
            cidStr;
        const matches = sortMatchesByPriceThenBuyer(
            applyPricingFilters(findPriceMatches(gradeId, gradeName, cidStr))
        );
        const pricedMatches = matches.filter(g =>
            hasAvailablePrice(g.price ?? g.Price ?? null)
        );

        pricedMatches.forEach(g => {
            const rawPrice = g.price ?? g.Price ?? null;
            rows.push([
                state.siteDescription ?? '',
                commodityName,
                gradeName,
                getBuyerName(g),
                parseFloat(rawPrice),
                g.sellingOptionDescription ?? g.SellingOptionDescription ?? '',
                g.paymentTermDescription ?? g.PaymentTermDescription ?? '',
            ]);
        });
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [24, 18, 12, 26, 12, 16, 34].map(wch => ({ wch }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Segregations');

    const pad = n => String(n).padStart(2, '0');
    const now = new Date();
    const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate()
    )}-${pad(now.getHours())}-${pad(now.getMinutes())}`;
    const siteName = String(state.siteDescription || 'Site')
        .trim()
        .replace(/[^a-z0-9]+/gi, '_')
        .replace(/^_+|_+$/g, '');
    const name = `Bunge_${siteName || 'Site'}_Segregations_${date}.xlsx`;

    const target = window !== window.top ? window.top : window;
    const url = target.URL.createObjectURL(
        new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], {
            type:
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
    );
    const a = Object.assign(target.document.createElement('a'), {
        href: url,
        download: name,
    });
    target.document.body.appendChild(a);
    a.click();
    target.document.body.removeChild(a);
    target.URL.revokeObjectURL(url);
}

async function init() {
    const loadingEl = $('[data-region="siteLoading"]');
    const errorEl = $('[data-region="siteError"]');
    const notFoundEl = $('[data-region="siteNotFound"]');
    const contentEl = $('[data-region="siteContent"]');
    const titleEl = $('[data-region="siteTitle"]');

    const showError = () => {
        hide(loadingEl);
        hide(contentEl);
        show(errorEl);
    };
    const showNotFound = () => {
        hide(loadingEl);
        hide(contentEl);
        show(notFoundEl);
    };
    const showContent = () => {
        hide(loadingEl);
        hide(errorEl);
        hide(notFoundEl);
        show(contentEl);
    };

    const rawName = resolveSiteName();
    if (!rawName) {
        showNotFound();
        return;
    }

    const siteDesc = rawName.toUpperCase();
    state.siteDescription = siteDesc;
    const displayName = resolveSiteDisplayName(rawName);

    if (titleEl) {
        titleEl.textContent = displayName;
    }
    setSiteTitle(displayName);
    updateOpenGraphTitle(displayName);

    try {
        const [siteGradeData, gradeData] = await Promise.allSettled([
            apiFetch(siteGradeUrl(siteDesc)),
            apiFetch(activePurchaseUrl(siteDesc)),
        ]);

        if (siteGradeData.status === 'rejected') throw siteGradeData.reason;

        const siteGradeItems = toArray(siteGradeData.value);
        if (!siteGradeItems.length) {
            showNotFound();
            return;
        }

        const siteId = siteGradeItems[0].SiteId ?? siteGradeItems[0].siteId;
        if (!siteId) {
            showError();
            return;
        }

        const gradeItems =
            gradeData.status === 'fulfilled' ? toArray(gradeData.value) : [];

        const siteRaw = await apiFetch(
            `${API.SITES}/${encodeURIComponent(siteId)}`
        );
        const site = Array.isArray(siteRaw)
            ? siteRaw[0]
            : Array.isArray(siteRaw?.value)
            ? siteRaw.value[0]
            : siteRaw.value ?? siteRaw;

        const { lat, lng } = resolveLatLng(site);
        if (isNaN(lat) || isNaN(lng)) {
            showError();
            return;
        }

        renderSiteUpdate(site);
        renderCard(site, lat, lng);
        showContent();

        await Promise.allSettled([
            initMap(lat, lng, siteDesc),
            (async () => {
                state.gradeItems = gradeItems;
                const result = await fetchCommodities(siteDesc);
                state.siteGradeItems = result.siteGradeItems;
                state.commodities = result.commodities;
                renderPricing(state.commodities, gradeItems, new Date());
            })(),
        ]);
    } catch {
        showError();
    }
}

if (root) init();
